import type { Options } from "highcharts";
import type * as React from "react";
import { useEffect, useRef, useState } from "react";

import * as _ from "lodash";
import { useDispatch } from "react-redux";

import { combineConfigs } from "@similarweb/pro-logic-components";

import { showErrorToast } from "app/actions/toast_actions";
import { ReactHighcharts } from "app/libraries/reactHighcharts";

import { withAnnotation } from "./components/annotations/AnnotationHOC";
import { CircularProgress } from "./components/annotations/Components/CircularProgress";
import type { IAnnotation } from "./components/annotations/Data/Annotation";
import { get } from "./components/annotations/Data/AnnotationService";
import { updateAnnotations, type UpdateType } from "./components/annotations/Helpers/DataHelper";
import defaultConfig from "./configs/defaultConfig";

const ReactHighchartsWithAnnotation = withAnnotation(ReactHighcharts);

export interface IChartProps {
    type: string;
    config?: any;
    data: any[];
    domProps?: any;
    afterRender?: (a?: FixmeType) => FixmeType;
    isFinalConfig?: boolean;
    chartIdForAnnotations?: string;
    showError?: (a?: FixmeType) => FixmeType;
    setChart?: (chartRef: FixmeType) => void;
}

const Chart: React.FC<IChartProps> = ({
    type,
    config = null,
    data,
    domProps = {
        style: {
            height: "100%",
        },
    },
    afterRender = _.identity,
    isFinalConfig = false,
    chartIdForAnnotations,
    setChart,
}) => {
    const dispatch = useDispatch();
    const showError = (text?: string) => dispatch(showErrorToast(text));
    const reactHC = useRef<FixmeType>(null);
    const [finalConfig, setFinalConfig] = useState<Options | null>(null);
    const [annotations, setAnnotations] = useState<IAnnotation[]>([]);
    const isAnnotationEnabled = () => {
        /** a temporary solution to hide the Annotation feature
         * we'll wait for feedback and then decide how to proceed
         */
        return false;
    };
    const [isLoadingAnnotation, setIsLoadingAnnotation] = useState(false);

    useEffect(() => {
        setConfig();
        if (isAnnotationEnabled()) {
            loadAnnotations();
        }
    }, [type, data, config, chartIdForAnnotations]);

    const setChartRef = (chartRef: FixmeType) => {
        if (!chartRef) {
            return;
        }
        setChart?.(chartRef);
        reactHC.current = chartRef?.container?.current;
    };

    const loadAnnotations = async () => {
        try {
            setIsLoadingAnnotation(true);
            const { annotations } = await get(chartIdForAnnotations ?? "");
            setAnnotations(annotations);
        } catch (e) {
            // temporarily removed following user complaints
            // until we find a better solution (SIM-35762)
            // showError(i18nFilter()("chart.annotations.loading.error"));
        } finally {
            setIsLoadingAnnotation(false);
        }
    };
    const handleAnnotationChange = (annotation: IAnnotation, changeType: UpdateType) => {
        setAnnotations(
            (prevAnnotations: IAnnotation[]) => updateAnnotations(prevAnnotations, annotation, changeType) ?? [],
        );
    };

    const setConfig = () => {
        let combinedFinalConfig;
        if (isFinalConfig) {
            combinedFinalConfig = combineConfigs({ data, type }, [defaultConfig, config]);
        } else {
            combinedFinalConfig = combineConfigs({ data, type }, [
                defaultConfig,
                config,
                {
                    series: data,
                },
            ]);
        }
        setFinalConfig(combinedFinalConfig);
    };

    if (isAnnotationEnabled()) {
        return (
            <div style={{ position: "relative" }}>
                {isLoadingAnnotation ? <CircularProgress /> : null}
                <ReactHighchartsWithAnnotation
                    allowChartUpdate
                    options={finalConfig}
                    containerProps={domProps}
                    callback={afterRender}
                    ref={setChartRef}
                    annotations={annotations}
                    onAnnotationChange={handleAnnotationChange}
                    chartIdForAnnotations={chartIdForAnnotations}
                />
            </div>
        );
    }
    return (
        <ReactHighcharts
            allowChartUpdate
            options={finalConfig}
            containerProps={domProps}
            callback={afterRender}
            ref={setChartRef}
        />
    );
};

export default Chart as React.FC<any>;
